import { ColumnSort, createColumnHelper } from "@tanstack/react-table";
import {
  AuthConfig,
  ControlCategories,
  cn,
  formatLocalDate,
  getControlCategoryName,
  getControlName,
  isDefensiveControl,
  isEndpointManagementControl,
  isVulnerabilityManagementControl,
  PartnerUsers,
  parseDate,
  queries,
  TimeFormats,
  useAuthConfig,
} from "@theprelude/core";
import { CheckmarkIcon, CloseIcon, Truncate, UserIcon } from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { ResponsiveContainer } from "@/components/responsive-container";
import {
  Column,
  endpointHostnameColumn,
  endpointSerialColumn,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import { None } from "@/components/table/unavaliable";
import { VirtualTable } from "@/components/table/virtual-table";
import { createQuery } from "@/lib/create-query";
import { ResultCount } from "../endpoint-tables/result-count";

const overlay = "user";

export const userSchema = z.tuple([
  z.literal(overlay),
  z.object({
    email: z.string(),
  }),
]);

export type UserSchema = NonNullable<z.infer<typeof userSchema>[1]>;

export function UserOverlay({ email }: UserSchema) {
  return (
    <DefaultOverlay title="User Details">
      <div className="h-full overflow-hidden p-4 pb-0">
        <UserDetails email={email} />
      </div>
    </DefaultOverlay>
  );
}

type PrimaryUser = PartnerUsers & {
  endpoints: PrimaryUserEndpoint[];
};

type EndpointCategory =
  | typeof ControlCategories.XDR
  | typeof ControlCategories.ASSET_MANAGER
  | typeof ControlCategories.VULN_MANAGER;

interface PrimaryUserEndpoint {
  endpoint_id: string;
  hostname: string | null;
  serialNumber: string | null;
  missing: Record<EndpointCategory, boolean>;
  controls: Record<EndpointCategory, string[]>;
}

const UserDetails = ({ email }: UserSchema) => {
  const authConfig = useAuthConfig();
  const { data: user } = userQuery(authConfig, email).useSuspenseQuery();

  const [columnSort, setColumnSort] = useState<ColumnSort[]>([]);

  const table = useTable({
    name: "user-devices",
    data: user.endpoints,
    columns,
    state: {
      sorting: columnSort,
    },
    onSortingChange: setColumnSort,
    getRowId: (row) => `${row.hostname}-${row.serialNumber}`,
  });

  const seenByInstances = user.instances.filter(
    (instance) => instance.last_seen,
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-2">
        <h1 className="flex items-center gap-2 text-2xl">
          <UserIcon className="text-secondary-foreground" />
          {user.email}
        </h1>
        {seenByInstances.length > 0 && (
          <ul className="max-w-xl divide-y divide-stroke-line border-b pb-1">
            {seenByInstances.map((instance) => (
              <li
                key={`${instance.control}-${instance.instance_id}`}
                className="flex items-center justify-between py-3"
              >
                <span>Last Seen by {getControlName(instance.control)}</span>
                {instance.last_seen &&
                  formatLocalDate(
                    parseDate(instance.last_seen),
                    TimeFormats.DATE_FIRST,
                  )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-4 pt-4">
        <div className="flex items-end justify-between">
          <strong className="font-medium text-base">Endpoints</strong>
          <ResultCount
            count={user.devices?.length ?? 0}
            totalCount={user.devices?.length ?? 0}
            resourceType="endpoint"
          />
        </div>
        <ResponsiveContainer className="flex-1 overflow-hidden">
          {({ height }) => (
            <VirtualTable
              table={table}
              height={height}
              emptyState={
                <span className="flex justify-center">
                  {user.email} is not the primary user of any devices.
                </span>
              }
            />
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const userQuery = (authConfig: AuthConfig, email: string) =>
  createQuery({
    queries: [
      queries.scm.users(authConfig, {
        query: { filter: { instances: { email } } },
      }),
      queries.scm.endpoints(authConfig, {
        query: {
          filter: {
            instances: { any: { primary_user_email: email } },
          },
        },
      }),
    ],
    queryFn: (usersData, endpointsData): PrimaryUser => {
      const user = usersData.data[0];

      if (!user) {
        throw new Error("User not found");
      }

      const endpoints: PrimaryUserEndpoint[] = endpointsData.data.map(
        (endpoint) => ({
          endpoint_id: endpoint.endpoint_id,
          hostname: endpoint.hostname,
          serialNumber: endpoint.serial_number,
          platform: endpoint.instances.find((i) => i.platform)?.platform,
          controls: {
            [ControlCategories.XDR]: endpoint.instances
              .filter((i) => isDefensiveControl(i.control))
              .map((i) => getControlName(i.control)),
            [ControlCategories.ASSET_MANAGER]: endpoint.instances
              .filter((i) => isEndpointManagementControl(i.control))
              .map((i) => getControlName(i.control)),
            [ControlCategories.VULN_MANAGER]: endpoint.instances
              .filter((i) => isVulnerabilityManagementControl(i.control))
              .map((i) => getControlName(i.control)),
          },
          missing: {
            [ControlCategories.XDR]: endpoint.missing_edr,
            [ControlCategories.ASSET_MANAGER]: endpoint.missing_asset_manager,
            [ControlCategories.VULN_MANAGER]: endpoint.missing_vuln_manager,
          },
        }),
      );

      return { ...user, endpoints };
    },
  });

const makeControlCategoryStateColumn = (
  id: string,
  category: EndpointCategory,
): Column<PrimaryUserEndpoint["missing"], PrimaryUserEndpoint> => ({
  id,
  enableSorting: false,
  cell: (info) => {
    const missing = info.getValue()[category];
    const controlNames = info.row.original.controls[category];
    return (
      <ControlCategoryState controlNames={controlNames} missing={missing} />
    );
  },
  size: 180,
  meta: {
    label: getControlCategoryName(category),
  },
});

const columnHelper = createColumnHelper<PrimaryUserEndpoint>();
const columns = [
  columnHelper.accessor("hostname", endpointHostnameColumn),
  columnHelper.accessor("serialNumber", endpointSerialColumn),
  columnHelper.accessor(
    "missing",
    makeControlCategoryStateColumn("missingEDR", ControlCategories.XDR),
  ),
  columnHelper.accessor(
    "missing",
    makeControlCategoryStateColumn(
      "missingEndpointManager",
      ControlCategories.ASSET_MANAGER,
    ),
  ),
  columnHelper.accessor(
    "missing",
    makeControlCategoryStateColumn(
      "missingVulnerabilityManager",
      ControlCategories.VULN_MANAGER,
    ),
  ),
];

const ControlCategoryState = ({
  controlNames,
  missing,
}: {
  controlNames: string[];
  missing: boolean;
}) => {
  const label = controlNames.toSorted().join(", ");

  return (
    <div
      className={cn(
        "flex items-center gap-1",
        missing && "text-secondary-foreground",
      )}
    >
      {missing ? (
        <CloseIcon className="text-secondary-foreground" />
      ) : (
        <CheckmarkIcon className="text-status-green" />
      )}
      {label ? <Truncate text={label}>{label}</Truncate> : <None />}
    </div>
  );
};
